import { useEffect, useState, useRef, useCallback } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Plus, Trash2, Loader2, ImagePlus, X } from "lucide-react";
import { api, BACKEND_URL, formatApiErrorDetail } from "@/lib/api";

const inputCls = "bg-black/50 border-white/15 text-white focus-visible:ring-theatre-gold focus-visible:border-theatre-gold rounded-none h-11";

export const PartnersManager = () => {
  const [partners, setPartners] = useState([]);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [logo, setLogo] = useState(null); // {file_id, filename, preview}
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api.get("/partners").then(({ data }) => setPartners(data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const onLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!["image/png", "image/jpeg", "image/webp", "image/svg+xml"].includes(f.type)) {
      toast.error("Format d'image invalide (PNG, JPG, WEBP ou SVG)."); return;
    }
    if (f.size > 5 * 1024 * 1024) { toast.error("Logo trop volumineux (5 Mo max)."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/partners/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogo({ file_id: data.file_id, filename: data.filename, preview: URL.createObjectURL(f) });
      toast.success("Logo téléversé.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setUploading(false); }
  };

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setLoading(true);
    try {
      const order = partners.length ? Math.max(...partners.map((p) => p.order)) + 1 : 0;
      const { data } = await api.post("/partners", {
        name: name.trim(), url: url.trim(), order, logo_file_id: logo?.file_id || null,
      });
      setPartners((p) => [...p, data]);
      setName(""); setUrl(""); setLogo(null);
      if (fileRef.current) fileRef.current.value = "";
      toast.success("Partenaire ajouté.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/partners/${id}`);
      setPartners((p) => p.filter((x) => x.id !== id));
      toast.success("Partenaire supprimé.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const move = async (id, dir) => {
    const idx = partners.findIndex((p) => p.id === id);
    const swap = idx + dir;
    if (swap < 0 || swap >= partners.length) return;
    const a = partners[idx], b = partners[swap];
    try {
      await Promise.all([
        api.patch(`/partners/${a.id}`, { order: b.order }),
        api.patch(`/partners/${b.id}`, { order: a.order }),
      ]);
      load();
    } catch { /* noop */ }
  };

  return (
    <div data-testid="partners-manager">
      <form onSubmit={add} className="bg-[#111] border border-white/10 p-6 mb-6" data-testid="partner-add-form">
        <div className="flex flex-col sm:flex-row gap-4 sm:items-end">
          <div className="flex-1 space-y-2">
            <label className="text-xs uppercase tracking-[0.15em] text-neutral-500">Nom du partenaire *</label>
            <Input data-testid="partner-name-input" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} placeholder="Ex : La Comédie-Française" />
          </div>
          <div className="flex-1 space-y-2">
            <label className="text-xs uppercase tracking-[0.15em] text-neutral-500">Lien (optionnel)</label>
            <Input data-testid="partner-url-input" value={url} onChange={(e) => setUrl(e.target.value)} className={inputCls} placeholder="https://…" />
          </div>
        </div>

        <div className="mt-4 flex items-center gap-4">
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={onLogo} data-testid="partner-logo-input" />
          {logo ? (
            <div className="flex items-center gap-3 border border-theatre-gold/40 bg-black/40 px-3 py-2">
              <img src={logo.preview} alt="logo" className="h-8 w-8 object-contain bg-white/5" />
              <span className="text-xs text-theatre-gold truncate max-w-[160px]">{logo.filename}</span>
              <button type="button" onClick={() => { setLogo(null); if (fileRef.current) fileRef.current.value = ""; }} className="text-neutral-400 hover:text-theatre-red"><X size={14} /></button>
            </div>
          ) : (
            <button type="button" data-testid="partner-logo-btn" onClick={() => fileRef.current?.click()} disabled={uploading} className="flex items-center gap-2 border border-white/20 hover:border-theatre-gold text-neutral-300 hover:text-theatre-gold px-4 h-11 text-xs uppercase tracking-[0.15em] transition-colors duration-200">
              {uploading ? <Loader2 className="animate-spin" size={15} /> : <ImagePlus size={15} />} Logo (optionnel)
            </button>
          )}
          <button type="submit" data-testid="partner-add-btn" disabled={loading} className="ml-auto bg-theatre-red hover:bg-theatre-redhover disabled:opacity-60 text-white px-6 h-11 text-xs uppercase tracking-[0.2em] transition-colors duration-200 flex items-center gap-2">
            {loading ? <Loader2 className="animate-spin" size={15} /> : <Plus size={15} />} Ajouter
          </button>
        </div>
      </form>

      <div className="border border-white/10" data-testid="partners-list">
        {partners.length === 0 && <p className="px-4 py-12 text-center text-neutral-600">Aucun partenaire.</p>}
        {partners.map((p, i) => (
          <div key={p.id} data-testid={`partner-row-${p.id}`} className="flex items-center gap-4 px-4 py-3 border-b border-white/10 last:border-0 hover:bg-white/[0.03]">
            <div className="flex flex-col text-neutral-600">
              <button onClick={() => move(p.id, -1)} disabled={i === 0} className="hover:text-theatre-gold disabled:opacity-20 leading-none">▲</button>
              <button onClick={() => move(p.id, 1)} disabled={i === partners.length - 1} className="hover:text-theatre-gold disabled:opacity-20 leading-none">▼</button>
            </div>
            {p.logo_url ? (
              <img src={`${BACKEND_URL}${p.logo_url}`} alt={p.name} className="h-10 w-14 object-contain bg-white/5 border border-white/10 shrink-0" />
            ) : (
              <div className="h-10 w-14 flex items-center justify-center bg-white/5 border border-white/10 text-neutral-700 shrink-0"><ImagePlus size={16} /></div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-white font-serif">{p.name}</p>
              {p.url && <p className="text-xs text-neutral-500 truncate">{p.url}</p>}
            </div>
            <button data-testid={`partner-delete-${p.id}`} onClick={() => remove(p.id)} className="p-2 border border-white/15 hover:border-theatre-red text-neutral-300 hover:text-theatre-red transition-colors duration-200">
              <Trash2 size={15} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
